/**
 * Declarative single-value drag targets.
 *
 * A perpendicular edge drag always resolves to "one scalar on the shape"
 * (room width, an alcove's depth, a chamfer's size, a free element's side).
 * Describing the target as data — instead of an apply closure captured in
 * drag state — keeps editor state serializable and every gesture replayable.
 */

import {
  CornerName,
  EdgeName,
  FloorPlanRoom,
  MIN_FREE_ELEMENT_SIZE,
  OuterWhich,
  setAlcoveDepth,
  setAlcoveLeftWall,
  setAlcoveOuterChamfer,
  setAlcoveRightWall,
  setFreeElementCornerChamfer,
  setFreeElementFrame,
  setRoomCornerChamfer,
  setRoomLength,
  setRoomWidth,
} from '@kaiizen/kai-floor-plan';
import type { Selection } from './editorState';

export type ValueTarget =
  | { t: 'roomWidth' }
  | { t: 'roomLength' }
  | { t: 'alcoveDepth'; edge: EdgeName; storageIndex: number }
  /** Left mouth wall: value = new position; width keeps `end` fixed. */
  | { t: 'alcoveLeft'; edge: EdgeName; storageIndex: number; end: number }
  /** Right mouth wall: value = new end (position + width). */
  | { t: 'alcoveRight'; edge: EdgeName; storageIndex: number }
  /** Chamfer diagonal: value scales (dx0, dy0) preserving their ratio. */
  | { t: 'chamferScale'; sel: ChamferSelection; dx0: number; dy0: number }
  /**
   * Free-element side: value = width (left/right) or length (back/front).
   * The opposite side stays fixed regardless of rotation: the center travels
   * (Δsize/2) along `u`, the dragged side's outward unit normal in room space.
   */
  | { t: 'freeSide'; index: number; side: EdgeName; cx0: number; cy0: number; size0: number; ux: number; uy: number };

export type ChamferSelection =
  | { kind: 'corner'; corner: CornerName }
  | { kind: 'alcoveCorner'; edge: EdgeName; index: number; which: OuterWhich }
  | { kind: 'innerCorner'; index: number; which: CornerName };

/**
 * Apply a dragged value to its target — PURE: returns a new shape (structural
 * sharing via the floor-plan edit catalog), the input is never mutated.
 * Gestures call this with the drag-START shape each move, so the drag is
 * always an absolute function of (base, value).
 */
export function applyValue(shape: FloorPlanRoom, target: ValueTarget, v: number): FloorPlanRoom {
  switch (target.t) {
    case 'roomWidth':
      return setRoomWidth(shape, v);
    case 'roomLength':
      return setRoomLength(shape, v);
    case 'alcoveDepth':
      return setAlcoveDepth(shape, target.edge, target.storageIndex, v);
    case 'alcoveLeft':
      return setAlcoveLeftWall(shape, target.edge, target.storageIndex, v);
    case 'alcoveRight':
      return setAlcoveRightWall(shape, target.edge, target.storageIndex, v);
    case 'chamferScale': {
      const d0 = chamferDiagValue(target.dx0, target.dy0);
      const k = d0 > 1e-9 ? v / d0 : 0;
      return applyChamfer(shape, target.sel, k * target.dx0, k * target.dy0);
    }
    case 'freeSide': {
      const r = shape.freeElements?.[target.index];
      if (!r) return shape;
      const size = Math.max(MIN_FREE_ELEMENT_SIZE, v);
      const d = (size - target.size0) / 2;
      const width = target.side === 'backEdge' || target.side === 'frontEdge' ? r.width : size;
      const length = target.side === 'backEdge' || target.side === 'frontEdge' ? size : r.length;
      return setFreeElementFrame(shape, target.index, {
        x: target.cx0 + d * target.ux - width / 2,
        y: target.cy0 + d * target.uy - length / 2,
        width,
        length,
      });
    }
  }
}

/** The scalar a chamfer diagonal drags: the corner's perpendicular depth. */
export function chamferDiagValue(dx: number, dy: number): number {
  const L = Math.hypot(dx, dy) || 1e-9;
  return dx * dy / L;
}

/**
 * Set a chamfer's legs on any chamferable corner (room / alcove / free
 * element) — PURE: returns a new shape via the catalog. Callers pass values
 * already clamped to the drag/chip maxes; the catalog setters re-clamp.
 */
export function applyChamfer(shape: FloorPlanRoom, sel: ChamferSelection, dx: number, dy: number): FloorPlanRoom {
  if (sel.kind === 'corner') return setRoomCornerChamfer(shape, sel.corner, { dx, dy });
  if (sel.kind === 'alcoveCorner') return setAlcoveOuterChamfer(shape, sel.edge, sel.index, sel.which, { dx, dy });
  return setFreeElementCornerChamfer(shape, sel.index, sel.which, { dx, dy });
}

export function isChamferSelection(sel: Selection): sel is ChamferSelection {
  return sel.kind === 'corner' || sel.kind === 'alcoveCorner' || sel.kind === 'innerCorner';
}
